import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { cn } from '@/lib/utils';

import { StudioMarkSlider } from './Furniture3DStudioWidget';

/**
 * Close-up crop widget (Variation · Close-up). The vendor draws a crop box over
 * the parent image to pick EXACTLY the region to zoom into, and chooses a lens +
 * depth of field. The normalized rect + lens/aperture are sent to the worker,
 * which crops that region and Flux-enhances it into a photoreal macro — so the
 * subject is deterministic (no vision-agent guessing).
 *
 * Lens + Depth of field render as collapsible option rows (matching the other
 * recipe-node inputs), each revealing the shared StudioMarkSlider.
 */

export interface NormCrop { x: number; y: number; w: number; h: number }

// Lens focal lengths (mm) — macro compression / feel. Continuous slider that
// snaps to these key stops.
const LENS_MARKS = [50, 85, 100, 135, 200];
// f-stops — depth of field / bokeh. Driven by index so the marks space evenly.
const APERTURES = [1.4, 2.8, 5.6, 11, 16];

function nearestApIndex(f: number): number {
    let bi = 0;
    for (let i = 1; i < APERTURES.length; i++) {
        if (Math.abs(APERTURES[i] - f) < Math.abs(APERTURES[bi] - f)) bi = i;
    }
    return bi;
}

/** "16:9" → 1.777…, undefined if unparseable/free. */
function parseAspect(s?: string): number | undefined {
    if (!s) return undefined;
    const [w, h] = s.split(':').map((n) => parseFloat(n));
    return w > 0 && h > 0 ? w / h : undefined;
}

export const CloseUpCropWidget: React.FC<{
    sourceImageUrl: string;
    crop?: NormCrop;
    lens: number;
    aperture: number;
    aspectRatio?: string;
    onCropChange: (c: NormCrop) => void;
    onLensChange: (mm: number) => void;
    onApertureChange: (f: number) => void;
}> = ({ sourceImageUrl, crop, lens, aperture, aspectRatio, onCropChange, onLensChange, onApertureChange }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<'lens' | 'dof' | null>(null);
    const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
    const aspectNum = parseAspect(aspectRatio);

    // Capture the image's natural dimensions as soon as they're known — via a
    // ref callback (covers an already-cached image whose onLoad can be missed)
    // AND onLoad (fresh loads). Needed to compute an aspect-correct crop box.
    const captureDims = (img: HTMLImageElement | null) => {
        if (img && img.naturalWidth) {
            setImgDims((d) => (d && d.w === img.naturalWidth ? d : { w: img.naturalWidth, h: img.naturalHeight }));
        }
    };

    // When the aspect ratio changes (or the image first loads), re-fit the crop
    // box to a centred rect of that ratio. The top aspect toolbar drives the
    // crop shape → the enhanced macro comes out at the chosen ratio. Manual
    // resizes keep the ratio locked (ReactCrop `aspect`) and don't re-trigger
    // this (guarded on the ratio string).
    // Seed the guard with the current ratio when a crop is already persisted, so
    // a reload keeps the vendor's positioned box instead of recentring it. A
    // fresh node (no crop) starts undefined → the effect lays down the first box.
    const lastAspectRef = useRef<string | undefined>(crop ? aspectRatio : undefined);
    useEffect(() => {
        if (!imgDims || !aspectNum) return;
        if (lastAspectRef.current === aspectRatio) return;
        lastAspectRef.current = aspectRatio;
        const c = centerCrop(
            makeAspectCrop({ unit: '%', width: 70 }, aspectNum, imgDims.w, imgDims.h),
            imgDims.w, imgDims.h,
        );
        onCropChange({ x: c.x / 100, y: c.y / 100, w: c.width / 100, h: c.height / 100 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aspectRatio, aspectNum, imgDims]);

    // Percent crop for ReactCrop derived from the stored normalized value
    // (default: centred 50% box).
    const pc: Crop = {
        unit: '%',
        x: (crop?.x ?? 0.25) * 100,
        y: (crop?.y ?? 0.25) * 100,
        width: (crop?.w ?? 0.5) * 100,
        height: (crop?.h ?? 0.5) * 100,
    };

    const apIndex = nearestApIndex(aperture);

    // Collapsible option row — mirrors the recipe-node input rows (label left,
    // current value + chevron right, expands to reveal the slider).
    const section = (
        id: 'lens' | 'dof',
        label: string,
        value: string,
        slider: React.ReactNode,
    ) => {
        const isOpen = open === id;
        return (
            <div>
                <div
                    onClick={() => setOpen(isOpen ? null : id)}
                    className={cn(
                        'flex cursor-pointer items-center justify-between border-t px-4 py-2 transition-colors hover:bg-accent',
                        isOpen && 'bg-muted',
                    )}
                >
                    <span className="text-sm font-medium tracking-[0.01em] text-muted-foreground">
                        {label}
                    </span>
                    <div className="flex items-center gap-1">
                        <span className="text-xs font-medium text-foreground">{value}</span>
                        <ChevronDown
                            className={cn(
                                'size-[18px] text-muted-foreground transition-transform',
                                isOpen && 'rotate-180',
                            )}
                        />
                    </div>
                </div>
                {isOpen && (
                    // stopPropagation on mousedown so dragging the slider rail moves
                    // the slider, not the canvas node (matches NodeShell convention).
                    <div
                        onMouseDown={(e) => e.stopPropagation()}
                        className="bg-muted px-4 pt-2 pb-1"
                    >
                        {slider}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {/* Crop picker */}
            <div className="px-4 pb-2.5">
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {t('CONTENT_STUDIO.CLOSEUP_CROP_HINT', 'Drag a box over the area to zoom into.')}
                </p>
                {/* Flex-centred, content-width frame so the box hugs the image —
                    no black letterbox around portrait / square sources. */}
                <div className="flex justify-center">
                    <div
                        onMouseDown={(e) => e.stopPropagation()}
                        className="overflow-hidden rounded-lg border leading-none [&_.ReactCrop]:block [&_img]:block [&_img]:max-h-[340px] [&_img]:max-w-full"
                    >
                        <ReactCrop
                            crop={pc}
                            aspect={aspectNum}
                            onChange={(_c, percent) => onCropChange({ x: percent.x / 100, y: percent.y / 100, w: percent.width / 100, h: percent.height / 100 })}
                            keepSelection
                            minWidth={8}
                            minHeight={8}
                        >
                            <img
                                src={sourceImageUrl}
                                alt="Source to crop"
                                ref={captureDims}
                                onLoad={(e) => captureDims(e.currentTarget)}
                            />
                        </ReactCrop>
                    </div>
                </div>
            </div>

            {/* Lens + Depth of field — collapsible option rows with sliders */}
            {section(
                'lens',
                t('CONTENT_STUDIO.CLOSEUP_LENS', 'Lens'),
                `${lens}mm`,
                <StudioMarkSlider
                    value={lens}
                    min={35}
                    max={200}
                    marks={LENS_MARKS.map((mm) => ({ value: mm, label: `${mm}` }))}
                    onChange={(v) => onLensChange(Math.round(v))}
                />,
            )}
            {section(
                'dof',
                t('CONTENT_STUDIO.CLOSEUP_DOF', 'Depth of field'),
                `f/${APERTURES[apIndex]}`,
                <StudioMarkSlider
                    value={apIndex}
                    min={0}
                    max={APERTURES.length - 1}
                    discrete
                    marks={APERTURES.map((f, i) => ({ value: i, label: `f/${f}` }))}
                    onChange={(i) => onApertureChange(APERTURES[Math.round(i)])}
                />,
            )}
        </div>
    );
};
